import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Plane, Ship, Truck, Trophy, Clock3, CheckCircle2 } from "lucide-react";

const ICON = { air: Plane, sea: Ship, road: Truck };
const LABEL = { air: "Air", sea: "Sea", road: "Road" };
const _defaults = {};
const getDefaults = async (m) => {
  if (!_defaults[m]) _defaults[m] = api.get(`/costing/defaults/${m}`).then((r) => r.data);
  return _defaults[m];
};
const usd = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const transit = (q) => {
  if (q.lead_weeks == null || q.lead_weeks === "") return q.lead_time || "—";
  const w = Number(q.lead_weeks), d = Math.round(w * 7);
  return q.lead_unit === "days" || !Number.isInteger(w) ? `${d} day${d === 1 ? "" : "s"}` : `${w} wk${w === 1 ? "" : "s"}`;
};

// Builds a costing config for mode m: keeps the user's shared inputs, swaps in m's own default charges.
export async function cfgFor(m, cfg, method) {
  const [dm, dc] = await Promise.all([getDefaults(m), getDefaults(method)]);
  const out = { ...cfg, method: m };
  Object.keys(dm).forEach((k) => { if (m === method ? false : JSON.stringify(dm[k]) !== JSON.stringify(dc[k])) out[k] = dm[k]; });
  return out;
}

function ModeCard({ row, best, fastest, onUse, selectedId }) {
  const Icon = ICON[row.mode] || Truck;
  const isSel = row.quote.id === selectedId;
  return (
    <div className={`flex flex-col rounded-xl border p-3.5 transition-shadow hover:shadow-sm ${isSel ? "border-[#1B4383] bg-[#F5F8FD] ring-1 ring-[#1B4383]/20" : "border-border bg-white"}`} data-testid={`rate-compare-${row.mode}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-[#1B4383]" />{LABEL[row.mode]}</span>
        <div className="flex flex-wrap justify-end gap-1">
          {best && <span className="inline-flex items-center gap-1 rounded-full bg-[#1B4383] px-2 py-0.5 text-[10px] font-semibold text-white"><Trophy className="h-3 w-3" />Cheapest</span>}
          {fastest && <span className="inline-flex items-center gap-1 rounded-full border border-[#1B4383]/30 px-2 py-0.5 text-[10px] font-semibold text-[#1B4383]"><Clock3 className="h-3 w-3" />Fastest</span>}
        </div>
      </div>
      <div className="font-mono text-lg font-semibold text-slate-900">{row.quote.currency || "USD"} {usd(row.quote.amount)}</div>
      <div className="text-[11px] text-muted-foreground">{row.quote.forwarder_name} · {transit(row.quote)}</div>
      {row.quote.notes && <div className="mt-1 line-clamp-2 text-[11px] italic text-slate-500" title={row.quote.notes} data-testid={`rate-compare-note-${row.mode}`}>{row.quote.notes}</div>}
      <div className="mt-2 flex justify-between border-t border-border pt-2 text-xs"><span className="text-muted-foreground">Landed (USD)</span><span className="font-mono font-semibold text-slate-900" data-testid={`rate-compare-landed-${row.mode}`}>{row.landed == null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : row.landed ? usd(row.landed) : "—"}</span></div>
      {row.others > 0 && <div className="mt-1 text-[11px] text-muted-foreground">+{row.others} more {LABEL[row.mode].toLowerCase()} quote{row.others === 1 ? "" : "s"}</div>}
      <div className="mt-3">
        {isSel ? <span className="inline-flex items-center gap-1 rounded-full bg-[#1B4383] px-2.5 py-1 text-[11px] font-semibold text-white"><CheckCircle2 className="h-3.5 w-3.5" />In Use</span>
          : <Button size="sm" variant="outline" className="h-8 w-full text-xs" onClick={() => onUse(row)} data-testid={`rate-compare-use-${row.mode}`}>Use {LABEL[row.mode]}</Button>}
      </div>
    </div>
  );
}

export function RateComparison({ rfqId, supplierId, freight, cfg, method, items, cocThreshold, onUse }) {
  const [rows, setRows] = useState([]);
  const quotes = freight?.forwarder_quotes || [];
  const modes = (freight?.methods || []).filter((m) => quotes.some((q) => q.method === m));
  const key = JSON.stringify([quotes.map((q) => [q.id, q.amount, q.method, q.lead_weeks]), cfg, items, method]);
  useEffect(() => {
    if (modes.length < 2 || !cfg) { setRows([]); return; }
    let live = true;
    const base = modes.map((m) => {
      const qs = quotes.filter((q) => q.method === m).sort((a, b) => a.amount - b.amount);
      return { mode: m, quote: qs[0], others: qs.length - 1, landed: null, leadWeeks: null };
    });
    setRows(base);
    const t = setTimeout(async () => {
      const done = await Promise.all(base.map(async (r) => {
        try {
          const c = await cfgFor(r.mode, cfg, method);
          c.freight_total = Number(r.quote.amount);
          const lc = (freight.local_costs_by_mode || {})[r.mode];
          if (lc) { c.transport_total = lc.transport_total; c.misc_total = lc.misc_total; }
          const { data } = await api.post(`/rfqs/${rfqId}/suppliers/${supplierId}/costing/compute`, { config: { ...c, coc_threshold: cocThreshold }, items });
          const landed = (data.rows || []).reduce((s, x) => s + (x.landed_after_finance || 0), 0);
          const sup = Number(cfg.supplier_lead_weeks);
          const supW = Number.isFinite(sup) && cfg.supplier_lead_weeks !== "" && cfg.supplier_lead_weeks != null ? ((cfg.supplier_lead_unit || "weeks") === "days" ? sup / 7 : sup) : null;
          const tr = r.quote.lead_weeks != null ? Number(r.quote.lead_weeks) : null;
          const leadWeeks = supW == null && tr == null ? null : Math.round(((supW || 0) + (tr || 0)) * 10) / 10;
          return { ...r, landed, leadWeeks };
        } catch { return { ...r, landed: 0 }; }
      }));
      if (live) setRows(done);
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (modes.length < 2 || !rows.length) return null;
  const ready = rows.every((r) => r.landed != null);
  const priced = ready && rows.some((r) => r.landed > 0);
  const minLanded = priced ? Math.min(...rows.map((r) => r.landed)) : Math.min(...rows.map((r) => Number(r.quote.amount)));
  const leads = rows.map((r) => r.leadWeeks).filter((v) => v != null);
  const minLead = leads.length > 1 ? Math.min(...leads) : null;
  return (
    <div className="mb-3" data-testid="rate-comparison">
      <div className={`grid gap-3 ${rows.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {rows.map((r) => (
          <ModeCard key={r.mode} row={r} best={ready && rows.length > 1 && (priced ? r.landed : Number(r.quote.amount)) === minLanded} fastest={minLead != null && r.leadWeeks === minLead}
            selectedId={freight.selected_quote_id} onUse={onUse} />
        ))}
      </div>
    </div>
  );
}
